import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {IconButton, Typography} from '@mui/material';
import {t} from 'i18next';
import {useLocation, useNavigate} from 'react-router-dom';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import _, {debounce} from 'lodash';
import {AxiosRequestConfig} from 'axios';
import AurBreadcrumb from '../../common/aur-breadcrumb/AurBreadCrumb';
import STRButtonBar from './component/STRButtonBar';
import STRKpiContainer from './component/STRKpiContainer';
import {useDispatch, useSelector} from 'react-redux';
import {useGridApiRef} from '@mui/x-data-grid-pro';
import {RootState} from '../../../redux/store';
import {setIndexingBannerData} from '../../../redux/slices/indexingManagementSlice';
import AurBanner from '../../common/aur-banner/AurBanner';
import {
  getParseSTRStatus,
  isConsiderCompletedSTR,
  STRStatusType,
} from './hook/useSTRStatus';
import STRSearchTermTable from './component/STRSearchTermTable';
import {useToast} from '../../../contexts/toastContext/ToastContext';
import {
  deleteSTRTerms,
  downloadSTR,
  getIndexByName,
  getSearchTermReportDetails,
  getSearchTermRunResults,
  runSTRTerms,
  stopSTRReport,
} from '../../../api/EcaServices';
import {showErrorToast} from '../../../api/utils';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import {EciHomePageTabs, SSE_ENDPOINT_ECA, SSE_SERVICE_TYPE} from '../../../constants/constants';
import {useEventSource} from '../../../hooks/sse/useEventSource';
import {
  downloadResponseAsXLSX,
  getProjectReportFileName,
} from '../../../utils/utils';
import AurDialog from '../../common/aur-dialog/AurDialog';
import STRBulkTaggingModal from './component/STRBulkTaggingModal';
import BulkTaggingModal, {
  TaggingFLowType,
} from '../../eca-doc-list/bulk-tagging/BulkTaggingModal';
import {deleteSTR} from '../../../api/STRService';
import STRReportStatusInfoContainer from './component/STRReportStatusInfoContainer';
import {checkAdditionalLoadByIndexUpdate} from './view-str-utils';
import {useAnalytics} from '../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_DATA_MODE_AUTO,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../constants/analyticsConstants';

export interface STRResultTermListType {
  term: string;
  termId: string;
  highlightColor: string;
  termRunstatus: STRStatusType;
  termDocHits: number;
  termDocHitsWithRGF: number;
  termUniqueHits: number;
  termUniqueHitsWithRGF: number;
  termLastRunTime: string;
  termLastUpdatedTime: string;
}

export interface STRResultKPIType {
  totalNumOfTerms?: number;
  totalDocHits?: number;
  totalDocHitsWithRGF?: number;
  totalDocsInSearchableSet?: number;
  totalErrorCount?: number;
  totalTermsCompleted?: number;
  reportRunStartTime?: string;
  reportRunEndTime?: string;
  hasPendingTerms?: boolean;
}

export interface STRTermRunResultsType extends STRResultKPIType {
  termList: STRResultTermListType[];
  reportRunStatus: STRStatusType;
  strRequestId?: string;
}

interface STRDetailType {
  strReportId: string;
  isDraftFlag: boolean;
  stage: string;
  scope: {
    name: string;
    indexName: string;
    searchId: string;
    notes: string;
    fieldTemplateId: number;
    savedSearchName?: string;
    externalEcaSearchId?: string;
  };
  terms: {
    term: string;
    highlightColor: string;
    isWPTerm?: boolean;
    termId?: string;
  }[];
  resultSettings: {
    includeUniqueHitsFlag: boolean;
    storeHitQueryStringsFlag: boolean;
    emailNotificationList: string[];
    relationalGroupFieldList: string[];
  };
}

type RUN_TERM_ACTION_TYPE = 'pending' | 'retryError' | 'all';
const stringPath = 'eca.searchTermReport.viewSTR.';

const getSSEPath = (requestID: string) => {
  return `${SSE_ENDPOINT_ECA}/sse/${requestID}/${SSE_SERVICE_TYPE.SEARCH_TERM_REPORT_SERVICE}`;
};

export const SSE_COMPLETED_EVENT_REFRESH_DATA_DELAY = 3000; // millisecond

const BulkTaggingOptionsLabel: Record<string, string> = {
  includeDocHits:
    'eca.searchTermReport.viewSTR.bulkTaggingModal.formContent.label1.header',
  includeDocHitsWithRGF:
    'eca.searchTermReport.viewSTR.bulkTaggingModal.formContent.label2.header',
  includeUniqueHitsFlag:
    'eca.searchTermReport.viewSTR.bulkTaggingModal.formContent.label3.header',
};

const ViewSTR = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {showToast} = useToast();
  const {trackGAEvent} = useAnalytics();
  const gridAPIRef = useGridApiRef(); // Create ref in parent
  const [strData, setSTRData] = useState<STRDetailType | null>(null);
  const [reqLoading, setReqLoading] = useState<boolean>(false);
  const [reportStatus, setReportStatus] = useState<STRStatusType | null>(null);
  const [termList, setTermList] = useState<STRResultTermListType[]>([]);
  const [allTerms, setAllTerms] = useState<STRResultTermListType[]>([]);
  const [kpiData, setKPIData] = useState<STRResultKPIType>({});
  const [reqResultTermLoading, setReqResultTermLoading] =
    useState<boolean>(false);
  // Track which run button is loading
  const [activeRunButton, setActiveRunButton] = useState<
    null | 'all' | 'pending' | 'retryError'
  >(null);
  const [haveResultTermReqError, setHaveResultTermReqError] =
    useState<boolean>(false);
  const [oDataQuery, setODataQuery] = useState<string | null>(null);
  const [totalTerms, setTotalTerms] = useState<number>(0);
  const [isConfirmAlertVisibleForRunTerm, setIsConfirmAlertVisibleForRunTerm] =
    useState<boolean>(false);
  const [showSTRBulkTaggingModal, setShowSTRBulkTaggingModal] =
    useState<boolean>(false);
  const [isBulkTaggingDrawerVisible, setIsBulkTaggingDrawerVisible] =
    useState<boolean>(false);
  const [selectedTermIds, setSelectedTermIds] = useState<string[]>([]);
  const [selectedTermGuIds, setSelectedTermGuIds] = useState<string[]>([]);
  const [selectedDocHitParam, setSelectedDocHitParam] = useState<string>('');
  const confirmAlertShowFor = useRef<{
    action: RUN_TERM_ACTION_TYPE;
    reason: 'index-updated' | 'found-wp-term' | 'none';
  }>({action: 'all', reason: 'none'});
  const bulkTaggingRef = useRef<HTMLButtonElement>(null);
  const apiResponseTime = useRef('');
  const [isEventTracked, setIsEventTracked] = useState(false);

  const {indexesBanner, selectedClient, reports} = useSelector(
    (state: RootState) => state
  );

  const {clientId} = selectedClient;
  const {projectId, selectedProjectName: projectName} = reports;

  const {indexBannerData: indexing} = indexesBanner;

  const locationQueryParam = useMemo(() => {
    const queryParam = new URLSearchParams(location.search);
    return {
      fieldTemplateId: queryParam.get('fieldTemplateId'),
      reportId: queryParam.get('id'),
      name: queryParam.get('name'),
      indexName: queryParam.get('indexName'),
      statusType: queryParam.get('statusType'),
    };
  }, [location.search]);

  const debounceFetchRunResult = useMemo(
    () =>
      debounce(
        (
          loader: boolean,
          isSSEConnectionCalled: boolean,
          isCompletedRun = false
        ) => {
          if (isCompletedRun) {
            setTimeout(() => {
              fetchSearchRunResults(
                loader,
                isSSEConnectionCalled,
                isCompletedRun
              );
              fetchSTRReportDetail(false);
            }, SSE_COMPLETED_EVENT_REFRESH_DATA_DELAY);
          } else {
            fetchSearchRunResults(loader, isSSEConnectionCalled);
          }
        },
        500
      ),
    [oDataQuery]
  );

  const isWPEnabledTermExist = useMemo(() => {
    return (strData?.terms ?? []).some((item) => item.isWPTerm);
  }, [strData]);

  useEffect(() => {
    if (locationQueryParam.fieldTemplateId && locationQueryParam.reportId) {
      fetchSTRReportDetail();
    } else {
      navigate('/welcome-single-client');
    }
  }, []);

  useEffect(() => {
    if (oDataQuery !== null) fetchSearchRunResults();
  }, [oDataQuery]);

  useEffect(() => {
    if (reportStatus === STRStatusType.InProgress) {
      dispatch(
        setIndexingBannerData({
          message: t(stringPath + 'topBannerMsg'),
          showIndexBanner: true,
        })
      );
    } else if (reportStatus === STRStatusType.Failed) {
      dispatch(
        setIndexingBannerData({
          message: t(stringPath + 'topProcessFailedBannerMsg'),
          showIndexBanner: true,
        })
      );
    } else
      dispatch(setIndexingBannerData({message: '', showIndexBanner: false}));

    if (reportStatus === STRStatusType.Completed) {
      showToast(
        t(stringPath + 'completedSuccessMsg', {
          name: strData?.scope.name ?? locationQueryParam.name,
        }),
        'success',
        undefined,
        'center'
      );
    }
    return () => {
      dispatch(setIndexingBannerData({message: '', showIndexBanner: false}));
    };
  }, [reportStatus]);

  const onSSEMessage = (event: MessageEvent) => {
    if (event.data && typeof event.data === 'string') {
      const response: {status: string; data: {TermStatus: string}} = JSON.parse(
        event.data
      );
      const {status, data} = response;
      if (
        getParseSTRStatus(status) === STRStatusType.Completed ||
        getParseSTRStatus(status) === STRStatusType.CompletedWithError ||
        getParseSTRStatus(status) === STRStatusType.Failed ||
        status === 'Cancelled' // To Handle Stop STR Case
      ) {
        closeSSE();
        debounceFetchRunResult(false, true, true);
      } else if (
        data?.TermStatus &&
        getParseSTRStatus(data.TermStatus) !== STRStatusType.InProgress
      ) {
        debounceFetchRunResult(false, true);
      }
    }
  };

  const {
    connect: connectSSE,
    close: closeSSE,
    isConnected,
  } = useEventSource(onSSEMessage);

  const fetchSTRReportDetail = async (loader = true) => {
    setReqLoading(loader);
    try {
      const queryParam = {
        clientId: clientId,
        projectId: projectId ?? '',
      };
      const response = await getSearchTermReportDetails(
        locationQueryParam.fieldTemplateId ?? '',
        locationQueryParam.reportId ?? '',
        new URLSearchParams(queryParam).toString()
      );
      const data: STRDetailType[] = _.get(response, 'data.data', []);
      if (data.length > 0) {
        setSTRData(data[0]);
      }
      apiResponseTime.current = response.data.responseTime;
    } catch (e) {
      console.debug('Handled error:', e); // added log to silent sonar warring
      showErrorToast(showToast, undefined);
      apiResponseTime.current = (e as any).responseTime;
    } finally {
      setReqLoading(false);
      !isEventTracked &&
        trackGAEvent({
          eventName: GA_EVENT_NAMES.PAGE_VIEW,
          page: GA_DATA_MODE_AUTO,
          user: GA_DATA_MODE_AUTO,
          str: {
            name: t('gaTracking.str.title'),
            index: locationQueryParam.indexName || '',
            status: locationQueryParam.statusType || '',
            elapsed_time: apiResponseTime.current,
          },
        });
      setIsEventTracked(true);
    }
  };

  const getStrResults = async (
    clientId: string,
    projectId: string,
    reportId: string,
    fieldTemplateId: string,
    oDataQuery: string | null | undefined = null
  ) => {
    // Build the query parameters
    const queryParams = {
      clientId: clientId,
      projectId: projectId,
    };
    let queryString = new URLSearchParams(queryParams).toString();

    // Apply paging, sorting, etc.
    if (oDataQuery) {
      queryString = `${queryString}&${oDataQuery}`;
    }

    return await getSearchTermRunResults(
      fieldTemplateId,
      reportId,
      queryString
    );
  };

  /**
   * to fetch latest run result of terms
   * @param loader pass true for show loader
   * @param isSSEConnectionCalled to determine sse connection is already initialize if true then don't again initialize
   * @param isSSECompletedEventReceived to determine received completed event from SSE and wait for Completed status from BE. Utill BE not send completed status in response, refresh api call every 3 seconds
   * @returns
   */
  const fetchSearchRunResults = async (
    loader = true,
    isSSEConnectionCalled = false,
    isSSECompletedEventReceived = false
  ) => {
    setReqResultTermLoading(loader);
    setHaveResultTermReqError(false);
    try {
      const pId = projectId ?? '';
      const reportId = locationQueryParam.reportId ?? '';
      const templateId = locationQueryParam.fieldTemplateId ?? '';
      const filteredResults = await getStrResults(
        clientId,
        pId,
        reportId,
        templateId,
        oDataQuery
      );

      const totalItem = _.get(
        filteredResults,
        'data.meta.pagination.totalItems',
        0
      );

      const filteredData: STRTermRunResultsType[] = _.get(
        filteredResults,
        'data.data',
        []
      );
      if (filteredData.length === 0) return;

      const {
        reportRunStatus,
        termList: termList,
        strRequestId,
        ...kpiObj
      } = filteredData[0];
      const latestRunStatus = getParseSTRStatus(reportRunStatus);

      // "Select All" feature requires all terms be present regardless of pagination.
      // Remove only paging query params.
      let oDataParams = oDataQuery?.split('&');
      oDataParams = oDataParams?.filter((i) => !i.startsWith('$top'));
      oDataParams = oDataParams?.filter((i) => !i.startsWith('$skip'));
      const queryParams = oDataParams?.join('&');
      const allResults = await getStrResults(
        clientId,
        pId,
        reportId,
        templateId,
        queryParams
      );
      const allData: STRTermRunResultsType[] = _.get(
        allResults,
        'data.data',
        []
      );
      const {termList: terms} = allData[0];
      setAllTerms(terms);

      /**
       * If received sse event with completed status and BE Still receiving In Progress status
       * then ignore the resposne and schedule next refresh call after 3 second get updated
       * report status. Ticket: https://consilio.atlassian.net/browse/ARHD-16113
       */
      if (
        latestRunStatus === STRStatusType.InProgress &&
        isSSECompletedEventReceived
      ) {
        setTimeout(() => {
          fetchSearchRunResults(
            loader,
            isSSEConnectionCalled,
            isSSECompletedEventReceived
          );
        }, SSE_COMPLETED_EVENT_REFRESH_DATA_DELAY);
        return;
      }

      setReportStatus(latestRunStatus);
      setTermList(termList);
      setKPIData(kpiObj);
      setTotalTerms(totalItem);

      if (strRequestId) {
        if (
          latestRunStatus === STRStatusType.InProgress &&
          !isConnected &&
          !isSSEConnectionCalled
        ) {
          connectSSE(getSSEPath(strRequestId));
        }
      }
      if (isConsiderCompletedSTR(latestRunStatus) && isConnected) {
        closeSSE();
      }
    } catch (e) {
      console.debug('Handled error:', e); // added log to silent sonar warring
      setHaveResultTermReqError(true);
      setTermList([]);
    } finally {
      setReqResultTermLoading(false);
    }
  };

  const deleteTermRequest = async (
    termIds: string[],
    onSuccess?: () => void
  ) => {
    setReqLoading(true);
    try {
      const queryParam = {
        clientId: clientId,
        projectId: projectId ?? '',
      };
      await deleteSTRTerms(
        locationQueryParam.fieldTemplateId ?? '',
        locationQueryParam.reportId ?? '',
        new URLSearchParams(queryParam).toString(),
        {terms: termIds}
      );
      showToast(
        t(stringPath + 'termDeleteSuccessMsg'),
        'success',
        undefined,
        'center'
      );
      gridAPIRef.current.setRowSelectionModel([]);
      setTimeout(() => {
        setReqLoading(false);
        if (onSuccess) {
          onSuccess();
        } else fetchSearchRunResults();
      }, 1000);

      // Run "Pending" terms after deleting one or more terms.
      await runSTRTermsRequest(false, 'pending');
    } catch (e) {
      showErrorToast(showToast, e);
      setReqLoading(false);
    }
  };

  const runSTRTermsRequest = async (
    runAll: boolean,
    action: RUN_TERM_ACTION_TYPE = 'all'
  ) => {
    setReqLoading(true);
    try {
      const queryParam = {
        clientId: clientId,
        projectId: projectId ?? '',
      };
      const response = await runSTRTerms(
        locationQueryParam.fieldTemplateId ?? '',
        locationQueryParam.reportId ?? '',
        new URLSearchParams(queryParam).toString(),
        {runAllTermsFlag: runAll}
      );
      const requestId = _.get(response, 'data.data.requestId', null);
      if (requestId) connectSSE(getSSEPath(requestId));
      fetchSearchRunResults(true, Boolean(requestId));

      let toastMsg = 'allTermRunSuccessMsg';
      if (action === 'retryError') toastMsg = 'retryErrorTermRunSuccessMsg';
      else if (action === 'pending') toastMsg = 'pendingTermRunSuccessMsg';
      showToast(t(stringPath + toastMsg), 'success', undefined, 'center');
    } catch (e) {
      showErrorToast(showToast, e);
    } finally {
      setReqLoading(false);
    }
  };

  const stopReport = async () => {
    setReqLoading(true);
    try {
      const queryParam = {
        clientId: clientId,
        projectId: projectId ?? '',
      };
      await stopSTRReport(
        locationQueryParam.fieldTemplateId ?? '',
        locationQueryParam.reportId ?? '',
        new URLSearchParams(queryParam).toString()
      );

      showToast(
        t(stringPath + 'stopSTRSuccessMessage', {
          name: strData?.scope.name ?? locationQueryParam.name,
        }),
        'success',
        undefined,
        'center'
      );

      // Check sse is connected then close the connection as report is stop successfully
      if (isConnected) {
        closeSSE();
      }
      debounceFetchRunResult(true, false, false);
    } catch (e) {
      console.debug('Handled error:', e); // added log to silent sonar warring
      showToast(
        t(stringPath + 'stopSTRReqFailed'),
        'error',
        undefined,
        'center'
      );
    } finally {
      setReqLoading(false);
    }
  };

  const fetchFieldTemplateIndexes = async () => {
    if (!projectId || !clientId) return;
    try {
      setReqLoading(true);
      const queryParam = {
        clientId: clientId,
        projectId: projectId ?? '',
      };
      const response = await getIndexByName(
        `?${new URLSearchParams(queryParam).toString()}`,
        locationQueryParam?.fieldTemplateId ?? ''
      );
      const data = _.get(response, 'data.data', []);
      setReqLoading(false);
      const isAdditionalLoadAdded = checkAdditionalLoadByIndexUpdate(
        data,
        kpiData.reportRunStartTime
      );
      if (isAdditionalLoadAdded || isWPEnabledTermExist) {
        /**
         * Storing reason to track all term required to run or only pending when user click on
         * continune button in alert.
         * if reason is index-updated then all term will be run
         * if reason is found-wp-term then only pending term will be run
         **/
        if (isAdditionalLoadAdded)
          confirmAlertShowFor.current.reason = 'index-updated';
        else if (isWPEnabledTermExist)
          confirmAlertShowFor.current.reason = 'found-wp-term';
        setIsConfirmAlertVisibleForRunTerm(true);
        trackGAEvent({
          eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
          subPage: {
            subpage_name: t('gaTracking.str.runPendingTermsModal'),
            subpage_type: GA_SUBPAGE_TYPE.MODAL,
          },
        });
      } else {
        runSTRTermsRequest(false, confirmAlertShowFor.current.action);
      }
    } catch (e) {
      setReqLoading(false);
      showErrorToast(showToast, e);
    }
  };

  const renderDismissBannerAction = () => {
    return (
      <IconButton
        className="typo-text buttonSmall_size"
        aria-label="close"
        onClick={() => {
          dispatch(
            setIndexingBannerData({message: '', showIndexBanner: false})
          );
        }}
        data-testid="dismiss-button"
      >
        <ChevronLeftIcon fontSize="small" className="dismiss-icon" />
        <Typography className="typo-text label2_size dismiss">
          {t('buttons.dismiss')}
        </Typography>
      </IconButton>
    );
  };

  const handleRunAllTermClick = async () => {
    setActiveRunButton('all');
    await runSTRTermsRequest(true, 'all');
  };

  const handleRunPendingTermClick = async () => {
    setActiveRunButton('pending');
    confirmAlertShowFor.current.action = 'pending';
    await fetchFieldTemplateIndexes();
  };

  const handleRetryErrorClick = async () => {
    setActiveRunButton('retryError');
    confirmAlertShowFor.current.action = 'retryError';
    await fetchFieldTemplateIndexes();
  };

  const handleDeleteReportClick = useCallback(async () => {
    if (projectId && clientId && strData) {
      try {
        setReqLoading(true);
        await deleteSTR(
          clientId,
          projectId,
          strData.strReportId,
          strData.scope.fieldTemplateId
        );
        showToast(
          t(stringPath + 'deleteSTRSuccessMessage', {
            name: strData?.scope.name ?? locationQueryParam.name,
          }),
          'success',
          undefined,
          'center'
        );
        navigate(
          `/eci-home-page/?tab=${EciHomePageTabs.STR}`
        );
      } catch (error: unknown) {
        showErrorToast(showToast, error, 'center');
      } finally {
        setReqLoading(false);
      }
    }
  }, [projectId, clientId, strData, locationQueryParam]);

  const handleExportReportClick = async () => {
    try {
      const queryParam = {
        clientId: clientId,
        projectId: projectId ?? '',
      };
      const requestConfig: AxiosRequestConfig = {
        responseType: 'blob',
      };
      setReqLoading(true);
      const result = await downloadSTR(
        locationQueryParam.fieldTemplateId ?? '',
        locationQueryParam.reportId ?? '',
        new URLSearchParams(queryParam).toString(),
        requestConfig
      );
      downloadResponseAsXLSX(
        result,
        getProjectReportFileName(
          `${projectName}_${locationQueryParam.name}`,
          t('eca.searchTermReport.STR')
        )
      );
    } catch (e) {
      console.debug('Handled error:', e); // added log to silent sonar warring
      showToast(
        t(stringPath + 'documentDownloadFailed'),
        'error',
        undefined,
        'center'
      );
    } finally {
      setReqLoading(false);
    }
  };

  const onConfirmAlertOkClick = () => {
    setIsConfirmAlertVisibleForRunTerm(false);
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('buttons.continue'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.MODAL,
        action: t('gaTracking.str.runPendingTermsOkAction'),
      },
      subPage: {
        subpage_name: t('gaTracking.str.runPendingTermsModal'),
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
    });
    const {action, reason} = confirmAlertShowFor.current;
    switch (reason) {
      case 'found-wp-term':
        runSTRTermsRequest(false, action);
        break;
      case 'index-updated':
        // Index is updated by aditional load so run all term pass true
        runSTRTermsRequest(true, action);
        break;
      default:
        break;
    }
  };

  const setShowBulkTaggingDrawer = (visible: boolean, isSuccess?: boolean) => {
    setIsBulkTaggingDrawerVisible(visible);
    if (visible)
      setTimeout(() => {
        bulkTaggingRef.current?.focus();
      }, 100);
    if (isSuccess) {
      gridAPIRef.current.setRowSelectionModel([]);
    }
  };

  const onConfirmTaggingHandler = (visible: boolean, selectedValue: string) => {
    setShowSTRBulkTaggingModal(false);
    setSelectedDocHitParam(selectedValue);
    setShowBulkTaggingDrawer(visible);
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.str.bulkTaggingOkAction'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.MODAL,
        action: t('gaTracking.str.confirmTagging'),
        details: t('gaTracking.str.taggingType', {
          type: t(BulkTaggingOptionsLabel[selectedValue]),
        }),
      },
      subPage: {
        subpage_name: t('gaTracking.str.selectBulkTagging'),
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
    });
    trackGAEvent({
      eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
      subPage: {
        subpage_name: t('gaTracking.str.bulkTaggingTags'),
        subpage_type: GA_SUBPAGE_TYPE.DRAWER,
      },
    });
  };

  const rednerDataForState = () => {
    if (
      reportStatus === STRStatusType.Draft ||
      reportStatus === STRStatusType.Ready
    ) {
      return (
        <div className="view-str-end">
          <Typography className="typo-text headline3_size reportStatus">
            {t(stringPath + 'reportToRunText')}
          </Typography>
        </div>
      );
    }
    return (
      <STRSearchTermTable
        status={reportStatus}
        reportName={strData?.scope.name ?? locationQueryParam.name}
        filteredTerms={termList}
        allTerms={allTerms}
        haveError={haveResultTermReqError}
        isIncludeUniqueHitsFlagColumnVisible={
          strData?.resultSettings.includeUniqueHitsFlag
        }
        isTermUniqueHitsWithRGFColumnVisible={
          (strData?.resultSettings.relationalGroupFieldList ?? []).length > 0
        }
        isDocsHitsWithRGFColumnVisible={
          (strData?.resultSettings.relationalGroupFieldList ?? []).length > 0
        }
        onODataQueryApply={setODataQuery}
        onErrorRefresh={fetchSearchRunResults}
        onDeleteSelectedTerm={deleteTermRequest}
        totalTerms={totalTerms}
        reportId={locationQueryParam.reportId ?? ''}
        fieldTemplateId={locationQueryParam.fieldTemplateId ?? ''}
        onTaggingSelectedTerm={setShowSTRBulkTaggingModal.bind(this, true)}
        onTermsSelection={(termIds) => {
          const selectedTerms = allTerms
            .filter((item) => termIds.includes(item.termId))
            .map((item) => item.term);

          setSelectedTermGuIds(
            allTerms
              .filter((item) => termIds.includes(item.termId))
              .map((item) => item.termId)
          );

          setSelectedTermIds(
            strData?.terms
              .filter((item) => selectedTerms.includes(item.term))
              .map((item) => item.termId ?? '') ?? []
          );
        }}
        gridAPIRef={gridAPIRef}
      />
    );
  };

  const getTotalHitsForSelectedTerms = (
    selectedTermIds: string[],
    termList: STRResultTermListType[],
    field: keyof STRResultTermListType
  ): number => {
    return termList.reduce((sum, term) => {
      return selectedTermIds.includes(term.termId)
        ? sum + (term[field] as number)
        : sum;
    }, 0);
  };

  const getDocCount = (): number => {
    const isAllTermsSelected = totalTerms === selectedTermIds.length;

    const kpiFallbacks: Record<string, number> = {
      includeDocHits: kpiData.totalDocHits ?? 0,
      includeDocHitsWithRGF: kpiData.totalDocHitsWithRGF ?? 0,
      includeUniqueHitsFlag: kpiData.totalDocsInSearchableSet ?? 0,
    };

    const termFieldMap: Record<string, keyof STRResultTermListType> = {
      includeDocHits: 'termDocHits',
      includeDocHitsWithRGF: 'termDocHitsWithRGF',
      includeUniqueHitsFlag: 'termUniqueHits',
    };

    const selectedField = termFieldMap[selectedDocHitParam];

    return isAllTermsSelected
      ? (kpiFallbacks[selectedDocHitParam] ?? 0)
      : getTotalHitsForSelectedTerms(
          selectedTermGuIds,
          termList,
          selectedField
        );
  };

  return (
    <>
      {indexing.showIndexBanner ? (
        <div className="view-str-top-banner">
          <AurBanner
            errorText={indexing.message}
            errorType={
              reportStatus === STRStatusType.Failed ? 'error' : 'warning'
            }
            hasFullWidth
            renderAction={
              reportStatus === STRStatusType.Failed
                ? renderDismissBannerAction
                : undefined
            }
          />
        </div>
      ) : null}
      <div className="view-str-main">
        <div className="view-str-header">
          <AurBreadcrumb
            items={[
              {
                label: t('eca.searchTermReport.STR'),
                path: `/eci-home-page/?tab=${EciHomePageTabs.STR}`,
                onClick: () =>
                  navigate(
                    `/eci-home-page/?tab=${EciHomePageTabs.STR}`
                  ),
              },
            ]}
          />
          <Typography className="typo-text headline1_size" tabIndex={0}>
            {strData?.scope.name ?? locationQueryParam.name}
          </Typography>
        </div>
        <div className="view-str-mid">
          <STRButtonBar
            status={reportStatus}
            onRunAllTerm={handleRunAllTermClick}
            onRunPendingTerm={handleRunPendingTermClick}
            onRetryError={handleRetryErrorClick}
            onExportReport={handleExportReportClick}
            onDeleteReport={handleDeleteReportClick}
            reportId={locationQueryParam.reportId ?? ''}
            fieldTemplateId={locationQueryParam.fieldTemplateId ?? ''}
            failedTermErrorCount={kpiData?.totalErrorCount ?? 0}
            havePendingToRun={kpiData?.hasPendingTerms ?? false}
            onStop={stopReport}
            totalNumberOfTerm={kpiData.totalNumOfTerms ?? 0}
            loadingButtonType={activeRunButton}
          />
          <STRReportStatusInfoContainer status={reportStatus} kpi={kpiData} />
          <STRKpiContainer
            data={kpiData}
            isDocsHitsWithRGFVisible={
              (strData?.resultSettings.relationalGroupFieldList ?? []).length >
              0
            }
          />
        </div>
        {rednerDataForState()}
      </div>
      <AurLoadingIndicator
        overlay={true}
        isOpen={reqLoading || reqResultTermLoading}
      />
      <AurDialog
        showDialog={isConfirmAlertVisibleForRunTerm}
        popupTitle={t(stringPath + 'confirmRunAlert.wantToContinue')}
        dialogTitle={t(stringPath + 'confirmRunAlert.justConfirm')}
        dismissable
        popupContent={<span>{t(stringPath + 'confirmRunAlert.message')}</span>}
        className="with-max-width view-str-confirm-dialog-run-term"
        onOk={onConfirmAlertOkClick}
        onCancel={() => {
          setIsConfirmAlertVisibleForRunTerm(false);
          trackGAEvent({
            eventName: GA_EVENT_NAMES.CLICK,
            click: {
              name: t('buttons.cancel'),
              type: GA_CLICK_TYPES.BUTTON,
              region: GA_REGIONS.MODAL,
              action: t('gaTracking.str.runPendingTermsCancelAction'),
            },
            subPage: {
              subpage_name: t('gaTracking.str.runPendingTermsModal'),
              subpage_type: GA_SUBPAGE_TYPE.MODAL,
            },
          });
        }}
        labelActionOk={t('buttons.continue')}
        labelActionCancel={t('buttons.cancel')}
      />
      <STRBulkTaggingModal
        status={reportStatus}
        visible={showSTRBulkTaggingModal}
        onClose={() => {
          setShowSTRBulkTaggingModal(false);
          trackGAEvent({
            eventName: GA_EVENT_NAMES.CLICK,
            click: {
              name: t('gaTracking.global.cancel'),
              type: GA_CLICK_TYPES.BUTTON,
              region: GA_REGIONS.MODAL,
              action: t('gaTracking.str.cancelTagging'),
            },
            subPage: {
              subpage_name: t('gaTracking.str.selectBulkTagging'),
              subpage_type: GA_SUBPAGE_TYPE.MODAL,
            },
          });
        }}
        onConfirmTagging={onConfirmTaggingHandler}
      />
      <BulkTaggingModal
        ref={bulkTaggingRef}
        showModal={isBulkTaggingDrawerVisible}
        handleModal={setShowBulkTaggingDrawer}
        selectdDocumentIds={selectedTermIds}
        allDocumentsCount={getDocCount()}
        flowType={TaggingFLowType.STR}
        selectedDocHitParam={selectedDocHitParam}
        fieldTemplateId={locationQueryParam.fieldTemplateId ?? undefined}
        strReportId={locationQueryParam.reportId ?? undefined}
      />
    </>
  );
};

export default ViewSTR;
